import { FC, useMemo } from 'react'
import { formatScore, getScoreColor } from 'react-common'
import { ActivityState, PresentationMode } from '@/api/models/activity'
import { LearnerObjectiveAccessCompactModel, ObjectiveWorkflowState } from '@/api/models/aggregate'
import { CourseComponent } from '@/components/courses/Course/CourseComponent'
import { useGetCourseCoverColor } from '@/hooks/useGetCourseCoverColor'
import { useGetCourseStateTranslator } from '@/hooks/useGetCourseStateTranslator'
import { usePrepareCourseInfo } from '@/hooks/usePrepareCourseInfo'
import { useAppSelector } from '@/store/hooks'
import { clamp } from '@/utils'
import { useGradingVisibility } from '@/views/courses/hooks/useGradingVisibility'
import { generateCourseLink } from './helpers/generateCourseLink'

type CourseProps = {
  aggregate: LearnerObjectiveAccessCompactModel
}

export const Course: FC<CourseProps> = ({ aggregate }) => {
  const searchText = useAppSelector((state) => state.aggregates.enrolledCoursesSearchText)

  const {
    finishedDate,
    startedDate,
    dueDate,
    availabilityDate,
    isNotStarted,
    submittedDate,
    isFinished,
    isInProgress,
  } = usePrepareCourseInfo(aggregate!)

  const coverUrl = useMemo(() => aggregate?.objective?.imageUrl, [aggregate])

  const progressValue = clamp(aggregate?.objectiveWorkflowAggregate.lastObjectiveWorkflow?.progress!, 0, 1) * 100

  const collections = useMemo(
    () => aggregate?.objective.showcases?.map((showcase) => showcase.name).join(', '),
    [aggregate],
  )

  const { showGrade, isSubmittedOrGrading, isCompletedBeforeGradeReveal, gradeDotColor, gradingLabel, finishedScore } =
    useGradingVisibility(aggregate)

  const badge = useMemo(() => {
    if (finishedScore) return finishedScore
    if (showGrade) return gradingLabel
    if (isCompletedBeforeGradeReveal) return gradingLabel

    return undefined
  }, [finishedScore, gradingLabel, isCompletedBeforeGradeReveal, showGrade])

  const score = useMemo(() => {
    const { lastObjectiveRecord } = aggregate?.objectiveWorkflowAggregate || {}
    return lastObjectiveRecord ? formatScore(lastObjectiveRecord)! : '-'
  }, [aggregate])

  const dotBackgroundColor = useMemo(() => {
    const { lastObjectiveRecord, objectiveGradingScale } = aggregate?.objectiveWorkflowAggregate || {}

    return lastObjectiveRecord && objectiveGradingScale
      ? getScoreColor(lastObjectiveRecord, objectiveGradingScale)
      : undefined
  }, [aggregate])

  const getStateTranslatedString = useGetCourseStateTranslator()

  const { isPassed, showPassed, state } = useMemo(() => {
    const { lastObjectiveRecord, lastObjectiveWorkflow } = aggregate?.objectiveWorkflowAggregate || {}

    const isPassed = lastObjectiveRecord?.passed

    const showPassed =
      lastObjectiveWorkflow?.state === ObjectiveWorkflowState.graded ||
      lastObjectiveWorkflow?.state === ObjectiveWorkflowState.finished ||
      !!lastObjectiveRecord?.finished

    const state = lastObjectiveWorkflow?.state
      ? getStateTranslatedString(lastObjectiveWorkflow.state)
      : getStateTranslatedString(ObjectiveWorkflowState.finished)

    return { isPassed, showPassed, state }
  }, [aggregate, getStateTranslatedString])

  const { id: aggregateId } = aggregate || {}
  const coverBackgroundColor = useGetCourseCoverColor(aggregateId)

  const link = generateCourseLink(aggregate)

  if (!aggregate || !link) {
    return null
  }

  return (
    <CourseComponent
      to={link}
      id={aggregate.objective.id}
      name={aggregate.objective.name}
      badge={badge}
      isFinished={isFinished}
      grade={aggregate?.objectiveWorkflowAggregate?.lastObjectiveRecord?.grade}
      score={score}
      showDetails={
        !!aggregate?.objectiveWorkflowAggregate?.lastObjectiveRecord &&
        !!aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.finished
      }
      coverUrl={coverUrl}
      isInProgress={isInProgress}
      isInGrading={isSubmittedOrGrading}
      progress={progressValue}
      finishedDate={finishedDate}
      startedDate={startedDate}
      submittedDate={submittedDate}
      dueDate={dueDate}
      availabilityDate={availabilityDate}
      dotBackgroundColor={finishedScore ? dotBackgroundColor : gradeDotColor}
      coverBackgroundColor={coverBackgroundColor!}
      isNotStarted={isNotStarted}
      subActivityCount={aggregate.objective.activity?.childrenCount ?? 0}
      showActivityCount={aggregate.objective.activity?.presentationMode === PresentationMode.Tiles}
      collections={collections}
      isPassed={isPassed}
      showPassed={showPassed}
      state={state}
      announcementChannelId={aggregate.objective.announcementChannelId}
      textToBeHighlighted={searchText}
      isDraft={aggregate.objective?.activity?.state === ActivityState.draft}
    />
  )
}
